"use client";
import React from "react";
import { motion } from "motion/react";
import {
  Package,
  Box,
  Utensils,
  ShoppingBag,
  Layers,
  Leaf,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { GlassCard } from "@/src/components/GlassCard";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import { useTranslation } from "react-i18next";
import Link from "next/link";

export const ProductsPage: React.FC = () => {
  const { t } = useTranslation("common");

  const products = [
    {
      icon: Package,
      title: t("products.customPrintedBoxes.title"),
      description: t("products.customPrintedBoxes.description"),
      image:
        "https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.customPrintedBoxes.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.customPrintedBoxes.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.customPrintedBoxes.customization", {
        returnObjects: true,
      }) as string[],
    },
    {
      icon: Box,
      title: t("products.corrugatedPackaging.title"),
      description: t("products.corrugatedPackaging.description"),
      image:
        "https://images.unsplash.com/photo-1739204618173-3e89def7140f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3JydWdhdGVkJTIwY2FyZGJvYXJkJTIwYm94ZXMlMjB3YXJlaG91c2V8ZW58MXx8fHwxNzcxNjA3OTQ1fDA&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.corrugatedPackaging.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.corrugatedPackaging.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.corrugatedPackaging.customization", {
        returnObjects: true,
      }) as string[],
    },
    {
      icon: Utensils,
      title: t("products.foodGradePackaging.title"),
      description: t("products.foodGradePackaging.description"),
      image:
        "https://images.unsplash.com/photo-1766050588070-b4ec0134ff8d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwZ3JhZGUlMjBwYWNrYWdpbmclMjBjb250YWluZXJzfGVufDF8fHx8MTc3MTYwNzk0Nnww&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.foodGradePackaging.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.foodGradePackaging.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.foodGradePackaging.customization", {
        returnObjects: true,
      }) as string[],
    },
    {
      icon: ShoppingBag,
      title: t("products.retailPackaging.title"),
      description: t("products.retailPackaging.description"),
      image:
        "https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.retailPackaging.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.retailPackaging.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.retailPackaging.customization", {
        returnObjects: true,
      }) as string[],
    },
    {
      icon: Layers,
      title: t("products.industrialBulkPackaging.title"),
      description: t("products.industrialBulkPackaging.description"),
      image:
        "https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.industrialBulkPackaging.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.industrialBulkPackaging.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.industrialBulkPackaging.customization", {
        returnObjects: true,
      }) as string[],
    },
    {
      icon: Leaf,
      title: t("products.ecoFriendlyPackaging.title"),
      description: t("products.ecoFriendlyPackaging.description"),
      image:
        "https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
      features: t("products.ecoFriendlyPackaging.features", {
        returnObjects: true,
      }) as string[],
      benefits: t("products.ecoFriendlyPackaging.benefits", {
        returnObjects: true,
      }) as string[],
      customization: t("products.ecoFriendlyPackaging.customization", {
        returnObjects: true,
      }) as string[],
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Our Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-walnut-900/90 via-walnut-800/80 to-walnut-600/70 dark:from-black/90 dark:via-walnut-900/85 dark:to-walnut-800/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t("products.hero.title")}
            </h1>
            <p className="text-xl text-walnut-100 max-w-3xl mx-auto">
              {t("products.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-20">
          {products.map((product, index) => (
            <div
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
            >
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? "md:order-2" : ""}
              >
                <GlassCard className="p-0 overflow-hidden">
                  <ImageWithFallback
                    src={product.image}
                    alt={product.title}
                    className="w-full h-96 object-cover"
                  />
                </GlassCard>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={index % 2 === 1 ? "md:order-1" : ""}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center mb-6">
                  <product.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-4">
                  {product.title}
                </h2>
                <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-6">
                  {product.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3">
                    {t("products.sectionTitles.features")}
                  </h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                        <span className="text-walnut-700 dark:text-walnut-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3">
                    {t("products.sectionTitles.benefits")}
                  </h3>
                  <ul className="space-y-2">
                    {product.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                        <span className="text-walnut-700 dark:text-walnut-300">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  href="/quote"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
                >
                  {t("common.getQuote")}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <GlassCard className="p-12 text-center">
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-6">
              {t("products.cta.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-8 max-w-2xl mx-auto">
              {t("products.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
              >
                {t("products.cta.contactUs")}
              </Link>
              <Link
                href="/quote"
                className="px-8 py-4 rounded-xl backdrop-blur-xl bg-walnut-100 dark:bg-walnut-800 text-walnut-900 dark:text-white font-semibold hover:bg-walnut-200 dark:hover:bg-walnut-700 transition-all duration-300 hover:scale-105"
              >
                {t("products.cta.requestQuote")}
              </Link>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
